// src/components/dashboard/EditAccessories.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { AddIcon } from "../../assets/icons/icons";
import CustomAlert from "../CustomAlert";
import { useNavigate, useParams } from "react-router-dom";

/* تعقيم كود اللون (#RGB/#RRGGBB) */
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};

export default function EditAccessories() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_BASE = "https://laravel-api.aj-mart.com/api";

  const [formData, setFormData] = useState({
    accessories_id: "",
    accessories_name_E: "",
    accessories_name_A: "",
    accessories_price: "",
    accessories_price_discount: "",
    accessories_tag: "",
    accessories_stock_available: "",
    accessories_features_E: "",
    accessories_features_A: "",
    accessories_desc_E: "",
    accessories_desc_A: "",
    accessories_color: [],       // ← أسماء الألوان فقط
    accessories_username: "",
    accessories_model: "",
  });

  // صور
  const [accessoriesMainImg, setAccessoriesMainImg] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [accessoriesImages, setAccessoriesImages] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);

  // خيارات السيلكت
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [modelsOptions, setModelsOptions] = useState([]);

  // القيم المختارة
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [selectedBrandName, setSelectedBrandName] = useState("");
  const [selectedModels, setSelectedModels] = useState([]); // array of model_id (numbers/strings)
  const [accessoriesUsername, setAccessoriesUsername] = useState("");

  // حالة عامة
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMsg, setAlertMsg] = useState({ type: "", message: "" });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // ===== ألوان ديناميكية (نفس AddAccessories) =====
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#000000");
  const [colorsWithHex, setColorsWithHex] = useState([]); // [{name, hex}]

  // helper: حول لأي دي لرقم لو أمكن
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : v;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  // جلب بيانات الإكسسوار + الخيارات
  useEffect(() => {
    axios
      .get(`${API_BASE}/accessories/${id}`)
      .then((response) => {
        const accessory = response.data.data;
        if (!accessory) throw new Error("Accessory data not found");

        // الأساسيات
        setFormData({
          accessories_id: accessory.accessories_id,
          accessories_name_E: accessory.accessories_name_E,
          accessories_name_A: accessory.accessories_name_A,
          accessories_price: accessory.accessories_price,
          accessories_price_discount: accessory.accessories_price_discount,
          accessories_tag: accessory.accessories_tag,
          accessories_stock_available: accessory.accessories_stock_available,
          accessories_features_E: accessory.accessories_features_E,
          accessories_features_A: accessory.accessories_features_A,
          accessories_desc_E: accessory.accessories_desc_E,
          accessories_desc_A: accessory.accessories_desc_A,
          accessories_color: [], // نضبط بعد قليل
          accessories_username: accessory.accessories_username,
          accessories_model: accessory.accessories_model,
        });

        // ========== الألوان ==========
        // 1) نحاول قراءة accessories_colors_with_hex إن متوفّر
        let namesFromHex = [];
        try {
          const parsedHex = JSON.parse(accessory.accessories_colors_with_hex || "null");
          if (Array.isArray(parsedHex) && parsedHex.length) {
            const mapped = parsedHex
              .map((c) => ({
                name: String(c?.color_name ?? c?.name ?? "").trim(),
                hex: sanitizeCssColor(c?.color_hex ?? c?.hex ?? ""),
              }))
              .filter((x) => x.name);
            setColorsWithHex(mapped);
            namesFromHex = mapped.map((x) => x.name);
          }
        } catch { /* ignore */ }

        // 2) fallback/merge: accessories_color قد تكون JSON أو CSV
        let names = [];
        try {
          const parsed = JSON.parse(accessory.accessories_color || "null");
          if (Array.isArray(parsed)) names = parsed.map((s) => String(s).trim()).filter(Boolean);
        } catch {
          names = String(accessory.accessories_color || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }

        // دمج الأسماء (مع إعطاء أولوية لقائمة HEX)
        const finalNames = namesFromHex.length
          ? Array.from(new Set([...names, ...namesFromHex]))
          : names;

        setFormData((p) => ({ ...p, accessories_color: finalNames }));

        // صور
        if (accessory.accessories_main_img) {
          setMainPreview(`${API_BASE}/storage/app/public/${accessory.accessories_main_img}`);
        }
        if (accessory.accessories_images) {
          try {
            const imagesArr = JSON.parse(accessory.accessories_images) || [];
            const fullUrls = imagesArr.map((img) => `${API_BASE}/storage/app/public/${img}`);
            setAdditionalPreviews(fullUrls);
          } catch { /* ignore */ }
        }

        setSelectedCategoryId(accessory.accessories_category_id?.toString() || "");
        setSelectedBrandId(accessory.brand_id?.toString() || "");
        setAccessoriesUsername(accessory.accessories_username || "");
      })
      .catch((error) => {
        console.error("Error fetching accessory:", error);
        setAlertMsg({ type: "error", message: "Failed to fetch accessory data" });
      });

    axios
      .get(`${API_BASE}/accessoriesCategory`)
      .then((response) => setCategoriesOptions(response.data.accessories_category || []))
      .catch((error) => console.error("Error fetching accessories categories:", error));

    axios
      .get(`${API_BASE}/brand`)
      .then((response) => setBrandsOptions(response.data.brands || []))
      .catch((error) => console.error("Error fetching brands:", error));

    axios
      .get(`${API_BASE}/accessoriesModel`)
      .then((response) => setModelsOptions(response.data.models || []))
      .catch((error) => console.error("Error fetching accessories models:", error));
  }, [id]);

  // أسماء التصنيفات/البراند
  useEffect(() => {
    if (selectedCategoryId && categoriesOptions.length > 0) {
      const cat = categoriesOptions.find(
        (c) => c.accessories_category_id?.toString() === selectedCategoryId
      );
      if (cat) setSelectedCategoryName(cat.accessories_category_name_E);
    }
  }, [selectedCategoryId, categoriesOptions]);

  useEffect(() => {
    if (selectedBrandId && brandsOptions.length > 0) {
      const brand = brandsOptions.find((b) => b.brand_id?.toString() === selectedBrandId);
      if (brand) setSelectedBrandName(brand.brand_name);
    }
  }, [selectedBrandId, brandsOptions]);

  // ====== التصفية بحسب البراند ======
  const filteredModels = useMemo(() => {
    const sid = selectedBrandId?.toString() || "";
    // لو الـ model ما فيه brand_id، نرجّع الكل (fallback)
    const hasBrandId = modelsOptions.some((m) => m.brand_id != null);
    if (!hasBrandId) return modelsOptions;
    return modelsOptions.filter((m) => (m.brand_id?.toString?.() || "") === sid);
  }, [modelsOptions, selectedBrandId]);

  const filteredModelIds = useMemo(
    () => filteredModels.map((m) => toNum(m.model_id)),
    [filteredModels]
  );

  // لما يتغير البراند أو الموديلات، نظّف الموديلات المختارة لتظل ضمن البراند الحالي
  useEffect(() => {
    if (!selectedBrandId) return;
    setSelectedModels((prev) => prev.filter((id) => filteredModelIds.includes(toNum(id))));
  }, [selectedBrandId, filteredModelIds]);

  // صور
  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAccessoriesMainImg(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    setAccessoriesImages(files);
    const previews = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(previews).then((results) => setAdditionalPreviews(results));
  };

  // تغيير حقول النص
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ===== ألوان ديناميكية (نفس AddAccessories) =====
  const addColor = () => {
    const name = (colorName || "").trim();
    if (!name) {
      setAlertMsg({ type: "error", message: "أدخل اسم اللون أولاً." });
      return;
    }
    const exists = formData.accessories_color.some(
      (c) => c.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setAlertMsg({ type: "error", message: "هذا اللون مُضاف مسبقًا." });
      return;
    }
    const hex = sanitizeCssColor(colorHex) || "";
    setFormData((p) => ({ ...p, accessories_color: [...p.accessories_color, name] }));
    setColorsWithHex((prev) => [...prev, { name, hex }]);
    setColorName("");
  };

  const removeColor = (name) => {
    setFormData((p) => ({
      ...p,
      accessories_color: p.accessories_color.filter(
        (c) => c.toLowerCase() !== name.toLowerCase()
      ),
    }));
    setColorsWithHex((prev) =>
      prev.filter((c) => c.name.toLowerCase() !== name.toLowerCase())
    );
  };

  const updateColorHex = (name, hexVal) => {
    const sanitized = sanitizeCssColor(hexVal) || "";
    setColorsWithHex((prev) =>
      prev.map((c) =>
        c.name.toLowerCase() === name.toLowerCase() ? { ...c, hex: sanitized } : c
      )
    );
  };

  const handleColorNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addColor();
    }
  };

  // موديلات
  const handleModelChange = (e, modelId) => {
    const idNum = toNum(modelId);
    if (e.target.checked) {
      setSelectedModels((prev) => (prev.includes(idNum) ? prev : [...prev, idNum]));
    } else {
      setSelectedModels((prev) => prev.filter((id) => toNum(id) !== idNum));
    }
  };

  // Submit
  const confirmUpdate = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (isSubmitting) return;

    setShowConfirmDialog(false);
    setIsSubmitting(true);

    const fd = new FormData();

    // 1) حقول النص/الأرقام (عدا color/model)
    Object.keys(formData).forEach((key) => {
      if (key === "accessories_color" || key === "accessories_model") return;
      fd.append(key, formData[key] ?? "");
    });

    // 2) الألوان (أسماء فقط) كسلسلة JSON
    fd.append(
      "accessories_color",
      JSON.stringify(formData.accessories_color || [])
    );

    // 2.1) ألوان مع HEX كـ JSON (اختياري/للاستخدام المستقبلي)
    const jsonColors = (formData.accessories_color || []).map((name) => {
      const found = colorsWithHex.find(
        (c) => c.name.toLowerCase() === name.toLowerCase()
      );
      return { name, hex: sanitizeCssColor(found?.hex || "") };
    });
    fd.append("accessories_colors_with_hex", JSON.stringify(jsonColors));

    // 3) الموديلات JSON
    fd.append("accessories_model", JSON.stringify(selectedModels || []));

    // 4) التصنيف والبراند
    fd.append("accessories_category_id", Number(selectedCategoryId) || "");
    fd.append("accessories_category", selectedCategoryName || "");
    fd.append("brand_id", Number(selectedBrandId) || "");
    fd.append("accessories_brand_name", selectedBrandName || "");
    fd.append("accessories_username", accessoriesUsername || "");

    // 5) الصور
    if (accessoriesMainImg) fd.append("accessories_main_img", accessoriesMainImg);
    (accessoriesImages || []).forEach((file) => {
      fd.append("accessories_images[]", file);
    });

    // 6) PUT عبر spoofing
    fd.append("_method", "PUT");

    try {
      const response = await axios.post(
        `${API_BASE}/accessories/update/${formData.accessories_id}`,
        fd
      );

      setAlertMsg({
        type: response.status === 200 ? "success" : "info",
        message: response.data?.message || "Accessory updated successfully",
      });

      setTimeout(() => navigate("/allAccessories"), 1200);
    } catch (error) {
      console.error("Error updating accessory:", error);
      setAlertMsg({
        type: "error",
        message:
          error?.response?.data?.details ||
          error?.response?.data?.message ||
          "An unexpected error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-[25px] flex flex-col gap-[50px]">
      <div className="main-title">Edit Accessory</div>

      {alertMsg.message && (
        <CustomAlert
          type={alertMsg.type}
          message={alertMsg.message}
          onClose={() => setAlertMsg({ type: "", message: "" })}
        />
      )}

      <form onSubmit={confirmUpdate}>
        {/* Basic Information */}
        <div className="flex flex-col gap-[15px]">
          <label className="text-black capitalize">Accessory Name In English</label>
          <input
            type="text"
            name="accessories_name_E"
            value={formData.accessories_name_E}
            onChange={handleFormChange}
            placeholder="Accessory Name In English"
            className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
            required
          />

          <label className="text-black capitalize">Accessory Name In Arabic</label>
          <input
            type="text"
            name="accessories_name_A"
            value={formData.accessories_name_A}
            onChange={handleFormChange}
            placeholder="Accessory Name In Arabic"
            className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
            required
          />

          <label className="text-black capitalize">Accessory Price</label>
          <input
            type="number"
            name="accessories_price"
            value={formData.accessories_price}
            onChange={handleFormChange}
            placeholder="Accessory Price"
            className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
            required
          />

          <label className="text-black capitalize">Accessory Tag</label>
          <input
            type="text"
            name="accessories_tag"
            value={formData.accessories_tag}
            onChange={handleFormChange}
            placeholder="Accessory Tag"
            className="px/[15px] py/[7px] border-2 rounded bg-[#eeeeee6e] text-black"
          />

          <label className="text-black capitalize">Accessory Stock Available</label>
          <input
            type="text"
            name="accessories_stock_available"
            value={formData.accessories_stock_available}
            onChange={handleFormChange}
            placeholder="Stock Available"
            className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
            required
          />

          <label className="text-black capitalize">Discount (%)</label>
          <input
            type="number"
            name="accessories_price_discount"
            value={formData.accessories_price_discount}
            onChange={handleFormChange}
            placeholder="Enter Discount Percentage"
            className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
          />
        </div>

        {/* Features & Descriptions */}
        <div className="flex gap-[10px] w-full">
          <div className="flex flex-col gap-[15px] w-1/2">
            <label className="text-black capitalize">Features (English)</label>
            <textarea
              name="accessories_features_E"
              value={formData.accessories_features_E}
              onChange={handleFormChange}
              placeholder="Features in English"
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black h-[200px]"
            />
          </div>
          <div className="flex flex-col gap-[15px] w-1/2">
            <label className="text-black capitalize">Features (Arabic)</label>
            <textarea
              name="accessories_features_A"
              value={formData.accessories_features_A}
              onChange={handleFormChange}
              placeholder="Features in Arabic"
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black h-[200px]"
            />
          </div>
        </div>

        <div className="flex gap-[10px] w-full">
          <div className="flex flex-col gap-[15px] w-1/2">
            <label className="text-black capitalize">Description (English)</label>
            <textarea
              name="accessories_desc_E"
              value={formData.accessories_desc_E}
              onChange={handleFormChange}
              placeholder="Description in English"
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black h-[200px]"
            />
          </div>
          <div className="flex flex-col gap-[15px] w-1/2">
            <label className="text-black capitalize">Description (Arabic)</label>
            <textarea
              name="accessories_desc_A"
              value={formData.accessories_desc_A}
              onChange={handleFormChange}
              placeholder="Description in Arabic"
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black h-[200px]"
            />
          </div>
        </div>

        {/* Category / Brand */}
        <div className="flex gap-[10px] w-full">
          <div className="flex flex-col gap-[15px] w-1/3">
            <label className="text-black capitalize">Select Accessories Category</label>
            <select
              value={selectedCategoryId}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCategoryId(value);
                const selectedCat = categoriesOptions.find(
                  (cat) => cat.accessories_category_id?.toString() === value
                );
                if (selectedCat) setSelectedCategoryName(selectedCat.accessories_category_name_E);
              }}
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
              required
            >
              <option value="" disabled>
                Select Category
              </option>
              {categoriesOptions.map((cat) => (
                <option key={cat.accessories_category_id} value={cat.accessories_category_id}>
                  {cat.accessories_category_name_E}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-[15px] w-1/3">
            <label className="text-black capitalize">Select Brand Name</label>
            <select
              value={selectedBrandId}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBrandId(value);
                const selectedBrand = brandsOptions.find((b) => b.brand_id?.toString() === value);
                if (selectedBrand) setSelectedBrandName(selectedBrand.brand_name);
              }}
              className="px-[15px] py-[7px] border-2 rounded bg-[#eeeeee6e] text-black"
              required
            >
              <option value="" disabled>
                Select Brand
              </option>
              {brandsOptions.map((brand) => (
                <option key={brand.brand_id} value={brand.brand_id}>
                  {brand.brand_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Models filtered by brand */}
        <div className="flex flex-col gap-[15px] w-full">
          <label className="text-black capitalize">Select Accessories Models</label>
          <div className="p-4 bg-gray-100 border-2 rounded">
            {selectedBrandId ? (
              filteredModels.length ? (
                <div className="flex flex-wrap gap-4">
                  {filteredModels.map((model) => (
                    <label key={model.model_id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        value={model.model_id}
                        checked={selectedModels.includes(toNum(model.model_id))}
                        onChange={(e) => handleModelChange(e, model.model_id)}
                        className="h-5 w-5 accent-blue-500 border-gray-300 rounded"
                      />
                      {model.model_name}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No models for the selected brand.</div>
              )
            ) : (
              <div className="text-sm text-gray-500">Select a brand to see its models.</div>
            )}
          </div>
        </div>

        {/* Colors — ديناميكي مثل AddAccessories */}
        <div className="flex gap-[10px] w-full">
          <div className="flex flex-col w-full">
            <label className="text-black capitalize">Colors</label>

            <div className="flex flex-col gap-3 p-4 bg-gray-100 border-2 rounded border-[#1485a6]">
              <div className="flex flex-wrap gap-3 items-center">
                <input
                  type="text"
                  placeholder="Color name (e.g. Midnight Blue)"
                  value={colorName}
                  onChange={(e) => setColorName(e.target.value)}
                  onKeyDown={handleColorNameKeyDown}
                  className="flex-1 min-w-[220px] px-3 py-2 border rounded"
                />
                <input
                  type="color"
                  title="Color HEX (optional)"
                  value={colorHex}
                  onChange={(e) => setColorHex(e.target.value)}
                  className="w-[48px] h-[40px] border rounded cursor-pointer"
                />
                <button
                  type="button"
                  onClick={addColor}
                  className="px-3 py-2 bg-[#1485a6] text-white rounded hover:opacity-90"
                >
                  Add Color
                </button>
              </div>

              {formData.accessories_color.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.accessories_color.map((name) => {
                    const obj =
                      colorsWithHex.find(
                        (c) => c.name.toLowerCase() === name.toLowerCase()
                      ) || { name, hex: "" };
                    return (
                      <div
                        key={name}
                        className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 shadow-sm"
                      >
                        <span
                          className="inline-block w-4 h-4 rounded-full border"
                          style={{ backgroundColor: obj.hex || "transparent" }}
                          title={obj.hex || "No HEX"}
                        />
                        <span className="text-sm font-medium">{name}</span>
                        <input
                          type="color"
                          value={obj.hex || "#000000"}
                          onChange={(e) => updateColorHex(name, e.target.value)}
                          className="w-[32px] h-[24px] border rounded cursor-pointer"
                          title="Change HEX"
                        />
                        <button
                          type="button"
                          onClick={() => removeColor(name)}
                          className="text-red-600 hover:text-red-700 text-sm font-semibold"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No colors added yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="flex gap-[10px] items-start w-full flex-wrap md:flex-nowrap">
          <div className="flex flex-col items-center w-full md:w-1/2">
            <label
              htmlFor="main-image-upload"
              className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center"
            >
              {mainPreview ? (
                <img src={mainPreview} alt="Main Preview" className="w-full h-full object-cover rounded" />
              ) : (
                "Main Image"
              )}
            </label>
            <input
              id="main-image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleMainImageChange}
            />
          </div>

          <div className="flex flex-col items-center w-full md:w-1/2">
            <label
              htmlFor="additional-images-upload"
              className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] text-black rounded flex items-center justify-center"
            >
              {additionalPreviews.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 w-full h-full p-2 overflow-y-auto">
                  {additionalPreviews.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-[80px] object-cover rounded"
                    />
                  ))}
                </div>
              ) : (
                "Upload Additional Images"
              )}
            </label>
            <input
              id="additional-images-upload"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleAdditionalImagesChange}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-[10px] mt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn flex items-center gap-[5px] w-full justify-center bg-[#1485a6] text-white px-4 py-2 rounded hover:bg-[#106c89] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AddIcon />
            <span>{isSubmitting ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </form>

      {/* Dialog التأكيد */}
      {showConfirmDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Update</h2>
            <p className="mb-4">Are you sure you want to update the accessory?</p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button className="btn bg-green-500 hover:bg-green-600" onClick={confirmUpdate}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
